/**
 * The app shell and its routes.
 *
 * The route table's shape encodes the security model, so it is worth reading
 * once carefully:
 *
 *   /g/:token   — **outside** every provider and guard. The public view must
 *                 render for someone with no session, so it cannot sit under
 *                 `RequireAuth`, and it must not sit under `GarageProvider`
 *                 either — that would fire `GET /garages` for a signed-out
 *                 visitor and 401 on a page that is working perfectly.
 *   /tools/*    — the same, for a different reason. The class calculator is a
 *                 reference tool that computes everything in the browser; there
 *                 is no account for it to need. Sitting out here is also what
 *                 makes that claim checkable, since nothing above it can fire a
 *                 request on its behalf.
 *   everything  — under `RequireAuth`, then `GarageProvider`. Nothing else in
 *   else          this app is reachable without a session and a tenant.
 */

import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { BrowserRouter, Navigate, NavLink, Route, Routes, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Car, CalendarDays, Flag, Gauge, LogOut, Settings, ShieldCheck, User, Users } from 'lucide-react';

import type { CarDto, DriverDto, EventDto, ItemDto } from '@svcdue/contracts';

import { trackPageview } from './lib/analytics';
import { useDashboard, useMe } from './api/hooks';
import { queryDefaults } from './api/hooks';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { RequireAuth } from './auth/SignIn';
import { AdminPanel } from './admin/AdminPanel';
import { CreateGarage, GarageSwitcher } from './garage/GarageSwitcher';
import { GarageProvider, useGarageContext } from './garage/GarageProvider';
import { Members } from './garage/Members';
import { ErrorState, Loading } from './components/states';
import { ScheduleProvider } from './lib/schedule';
import { AddCar } from './flows/AddCar';
import { AddDriver } from './flows/AddDriver';
import { AddEvent } from './flows/AddEvent';
import { AddGear } from './flows/AddGear';
import { AddPart } from './flows/AddPart';
import { SafetyKit } from './flows/SafetyKit';
import type { SafetyOwner } from './flows/safety-kit';
import { Account } from './screens/Account';
import { ClassCalc } from './screens/ClassCalc';
import { Dashboard } from './screens/Dashboard';
import { DriverScreen } from './screens/Driver';
import { Garage } from './screens/Garage';
import { PublicGarage } from './screens/Public';
import { Privacy, Terms } from './screens/Legal';
import { Timeline } from './screens/Timeline';
import { Footer } from './components/Footer';
import { daysUntil } from './lib/format';

/**
 * One client for the app's lifetime. Created at module scope rather than in the
 * component: a client rebuilt on re-render would drop the whole cache each time,
 * which turns every state change into a full refetch.
 */
const queryClient = new QueryClient({ defaultOptions: { queries: queryDefaults } });

/**
 * Records an anonymous pageview on every client-side navigation.
 *
 * It sits INSIDE BrowserRouter (useLocation requires a router) but OUTSIDE Routes and every
 * guard, deliberately: measurement must not depend on being signed in, and must not be able to
 * affect what renders. It returns null and reads nothing but the pathname.
 *
 * The pathname is mapped to an allowlisted key before it leaves the browser — /g/:token carries
 * a share secret that must never become a metric name. See lib/analytics.ts.
 */
export function PageviewTracker(): null {
  const { pathname } = useLocation();
  useEffect(() => {
    trackPageview(pathname);
  }, [pathname]);
  return null;
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <PageviewTracker />
        <Routes>
          {/* No auth, no providers — see the note at the top of this file.
              Terms and Privacy sit out here too: a signed-out visitor, or
              someone on a public garage link, must be able to reach them from
              the footer. */}
          <Route path="/g/:token" element={<PublicGarage />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          {/* Two routes, one page: the calculator's catalogue is deep-linkable,
              and the default catalogue keeps the bare path it has always had.
              Anything else under /tools/classcalc/ redirects — see
              classcalc/catalogue.ts. */}
          <Route path="/tools/classcalc" element={<ClassCalc />} />
          <Route path="/tools/classcalc/:catalogue" element={<ClassCalc />} />
          <Route
            path="*"
            element={
              <AuthProvider>
                <RequireAuth>
                  <GarageProvider>
                    <ScheduleProvider>
                      <Shell />
                    </ScheduleProvider>
                  </GarageProvider>
                </RequireAuth>
              </AuthProvider>
            }
          />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

type Modal =
  | null
  | { kind: 'addCar' }
  | { kind: 'editCar'; car: CarDto }
  | { kind: 'addPart'; car: CarDto }
  | { kind: 'editPart'; car: CarDto; item: ItemDto }
  // Gear is an item owned by a driver, so it reuses the same AddPart flow — only
  // the owner differs (a driver instead of a car).
  | { kind: 'addGear'; driver: DriverDto }
  | { kind: 'editGear'; driver: DriverDto; item: ItemDto }
  | { kind: 'addEvent' }
  | { kind: 'editEvent'; event: EventDto }
  | { kind: 'addDriver' }
  | { kind: 'editDriver'; driver: DriverDto }
  // The NHRA safety-equipment starter, opened from a car or a driver — the owner
  // is all it needs, so one modal serves both.
  | { kind: 'safetyKit'; owner: SafetyOwner };

function Shell() {
  const { garage, isPending, isError, error, refetch, isEmpty } = useGarageContext();
  const [modal, setModal] = useState<Modal>(null);

  if (isPending) return <Loading page label="Loading your garages…" />;
  /**
   * The garage list failed, so there is no TopBar and no Nav to render — but
   * this is a state a user can sit in indefinitely (a retry that keeps failing),
   * not a flicker on the way somewhere. It gets the Footer for the same reason
   * every other terminal screen does: Terms and Privacy have to be reachable
   * from wherever the user actually is.
   */
  if (isError) {
    return (
      <div className="svcdue">
        <main className="main">
          <ErrorState error={error} onRetry={refetch} />
        </main>
        <Footer />
      </div>
    );
  }

  /**
   * A signed-in user with no garage is not an error state — it is every new
   * account's first second. The create flow *is* the screen, with no nav around
   * it, because there is nothing yet to navigate.
   *
   * It still needs a sign-out, though: the full TopBar does not render here (it
   * reads a garage that does not exist yet), so without this a new user — or
   * someone who signed in with the wrong Google account — is trapped on the
   * create screen with no way back out.
   */
  if (isEmpty || !garage) {
    return (
      <div className="svcdue">
        <SignedOutBar />
        <main className="main">
          <CreateGarage standalone />
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="svcdue">
      <TopBar />
      <Nav />

      <main className="main">
        <Routes>
          <Route
            path="/"
            element={
              <Dashboard
                openAddCar={() => setModal({ kind: 'addCar' })}
                openAddEvent={() => setModal({ kind: 'addEvent' })}
              />
            }
          />
          <Route
            path="/garage"
            element={
              <Garage
                openAddCar={() => setModal({ kind: 'addCar' })}
                openEditCar={(car) => setModal({ kind: 'editCar', car })}
                openAddPart={(car) => setModal({ kind: 'addPart', car })}
                openEditPart={(car, item) => setModal({ kind: 'editPart', car, item })}
                openAddSafety={(car) =>
                  setModal({ kind: 'safetyKit', owner: { ownerType: 'car', ownerId: car.id, label: car.nickname } })
                }
              />
            }
          />
          <Route
            path="/garage/:carId"
            element={
              <Garage
                openAddCar={() => setModal({ kind: 'addCar' })}
                openEditCar={(car) => setModal({ kind: 'editCar', car })}
                openAddPart={(car) => setModal({ kind: 'addPart', car })}
                openEditPart={(car, item) => setModal({ kind: 'editPart', car, item })}
                openAddSafety={(car) =>
                  setModal({ kind: 'safetyKit', owner: { ownerType: 'car', ownerId: car.id, label: car.nickname } })
                }
              />
            }
          />
          <Route
            path="/timeline"
            element={
              <Timeline
                openAddEvent={() => setModal({ kind: 'addEvent' })}
                openEditEvent={(event) => setModal({ kind: 'editEvent', event })}
              />
            }
          />
          <Route
            path="/driver"
            element={
              <DriverScreen
                openAddDriver={() => setModal({ kind: 'addDriver' })}
                openEditDriver={(driver) => setModal({ kind: 'editDriver', driver })}
                openAddGear={(driver) => setModal({ kind: 'addGear', driver })}
                openEditGear={(driver, item) => setModal({ kind: 'editGear', driver, item })}
                openAddSafety={(driver) =>
                  setModal({ kind: 'safetyKit', owner: { ownerType: 'driver', ownerId: driver.id, label: driver.name } })
                }
              />
            }
          />
          <Route path="/crew" element={<Members />} />
          <Route path="/account" element={<Account />} />
          <Route path="/admin" element={<AdminPanel />} />
          {/* An unknown path is a typo or a stale bookmark, not a 404 worth a
              screen — send them somewhere useful. */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </main>

      <Footer />

      {modal?.kind === 'addCar' && <AddCar garageId={garage.id} onClose={() => setModal(null)} />}
      {modal?.kind === 'editCar' && <AddCar garageId={garage.id} car={modal.car} onClose={() => setModal(null)} />}
      {modal?.kind === 'addPart' && (
        <AddPart
          garageId={garage.id}
          owner={{ ownerType: 'car', ownerId: modal.car.id, label: modal.car.nickname }}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'editPart' && (
        <AddPart
          garageId={garage.id}
          owner={{ ownerType: 'car', ownerId: modal.car.id, label: modal.car.nickname }}
          item={modal.item}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'addGear' && (
        <AddGear garageId={garage.id} driverId={modal.driver.id} driverName={modal.driver.name} onClose={() => setModal(null)} />
      )}
      {modal?.kind === 'editGear' && (
        <AddGear
          garageId={garage.id}
          driverId={modal.driver.id}
          driverName={modal.driver.name}
          item={modal.item}
          onClose={() => setModal(null)}
        />
      )}
      {modal?.kind === 'addEvent' && <AddEvent garageId={garage.id} onClose={() => setModal(null)} />}
      {modal?.kind === 'editEvent' && <AddEvent garageId={garage.id} event={modal.event} onClose={() => setModal(null)} />}
      {modal?.kind === 'addDriver' && <AddDriver garageId={garage.id} onClose={() => setModal(null)} />}
      {modal?.kind === 'editDriver' && <AddDriver garageId={garage.id} driver={modal.driver} onClose={() => setModal(null)} />}
      {modal?.kind === 'safetyKit' && <SafetyKit garageId={garage.id} owner={modal.owner} onClose={() => setModal(null)} />}
    </div>
  );
}

/**
 * The brand and a sign-out, and nothing else.
 *
 * For the states that render outside the full TopBar — today the no-garage
 * create screen. It deliberately shows the signed-in email: the most common
 * reason to be here and want out is having signed in with the wrong Google
 * account, and seeing which one is what tells you.
 */
export function SignedOutBar() {
  const { signOut, session } = useAuth();

  return (
    <header className="topbar">
      <div className="brand">
        <span className="brand-mark">
          <span className="bm-b bm-a" />
          <span className="bm-b bm-a" />
          <span className="bm-b bm-g" />
        </span>
        <div className="brand-txt">
          <b>SvcDue</b>
          <span>race maintenance</span>
        </div>
      </div>

      <div className="topbar-user">
        {session && <span className="topbar-email">{session.claims.email}</span>}
        <button className="act act-ghost" onClick={signOut} aria-label="Sign out">
          <LogOut size={13} /> Sign out
        </button>
      </div>
    </header>
  );
}

function TopBar() {
  const { signOut, session } = useAuth();
  const { garageId } = useGarageContext();
  const dashboard = useDashboard(garageId);
  const next = dashboard.data?.nextEvent;

  return (
    <header className="topbar">
      <div className="brand">
        <span className="brand-mark">
          <span className="bm-b bm-a" />
          <span className="bm-b bm-a" />
          <span className="bm-b bm-g" />
        </span>
        <div className="brand-txt">
          <b>SvcDue</b>
          <span>race maintenance</span>
        </div>
      </div>

      <GarageSwitcher />

      {next && (
        <div className="topbar-next">
          <Flag size={13} />
          <div>
            <span>Next event</span>
            <b>
              {daysUntil(next.date)}d · {next.name}
            </b>
          </div>
        </div>
      )}

      <div className="topbar-user">
        {session && <span className="topbar-email">{session.claims.email}</span>}
        {/* Account settings — today, self-service deletion (§11). A quiet icon
            rather than a nav tab: it is reached rarely and deliberately. */}
        <NavLink to="/account" className="act act-ghost" aria-label="Account settings">
          <Settings size={13} />
        </NavLink>
        <button className="act act-ghost" onClick={signOut} aria-label="Sign out">
          <LogOut size={13} />
        </button>
      </div>
    </header>
  );
}

function Nav() {
  const { isSignedIn } = useAuth();
  const me = useMe(isSignedIn);
  const { role } = useGarageContext();

  return (
    <nav className="nav">
      <NavItem to="/" icon={<Gauge size={15} />} label="Dashboard" end />
      <NavItem to="/garage" icon={<Car size={15} />} label="Garage" />
      <NavItem to="/timeline" icon={<CalendarDays size={15} />} label="Timeline" />
      <NavItem to="/driver" icon={<User size={15} />} label="Driver" />

      {/* Crew have no member list and no share toggle, so the tab that holds
          both would open on an empty screen (§6). */}
      {/* Owner-only, and holds sharing as well as crew — the label says so,
          because "Crew" alone gave no hint that the make-public toggle lived
          here and owners went looking for it. */}
      {role === 'owner' && <NavItem to="/crew" icon={<Users size={15} />} label="Share & crew" />}

      {me.data?.user.isAdmin && <NavItem to="/admin" icon={<ShieldCheck size={15} />} label="Admin" />}
    </nav>
  );
}

function NavItem({ to, icon, label, end }: { to: string; icon: ReactNode; label: string; end?: boolean }) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => 'nav-btn' + (isActive ? ' nav-on' : '')}>
      {icon} <span>{label}</span>
    </NavLink>
  );
}
